import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { type ColumnDef, DataTable } from '~/components/composites/data-table'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select'
import { useDebounce } from '~/hooks/use-debounce'
import { useMutation } from '~/hooks/use-mutation'
import { useQuery } from '~/hooks/use-query'
import { useToast } from '~/hooks/use-toast'
import { useAppStore } from '~/stores/app'
import { DEFAULT_PAGE_SIZE, DialogId, type ListResponse } from '~/types/common'
import { MutationKey } from '~/types/mutation-key'
import { QueryKey } from '~/types/query-key'
import type { School, SchoolMirrorSyncResult } from '~/types/school'

type SchoolsListProps = {
  onEdit: (item: School) => void
}

export function SchoolsList({ onEdit }: SchoolsListProps) {
  const { t } = useTranslation()
  const { showSuccess } = useToast()
  const openDialog = useAppStore((state) => state.openDialog)

  // Manual schools -> skills mirror reconcile (HER-349 phase 2). The mirror
  // is already maintained transactionally by every school write; this is an
  // idempotent escape hatch for drift introduced outside the app (direct
  // SQL, restores) — see apps/server/docs/schools/sync-mirrors.md.
  const { mutate: syncMirrors, isPending: isSyncing } = useMutation<SchoolMirrorSyncResult, undefined>({
    mutationKey: MutationKey.SYNC_SCHOOL_MIRRORS,
    method: 'POST',
    options: {
      onSuccess: (result) => {
        showSuccess(
          t('schools.syncComplete', {
            created: result.created,
            updated: result.updated,
            unchanged: result.unchanged,
          }),
        )
      },
    },
  })

  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<string>('all')
  const [page, setPage] = useState(1)
  const debouncedSearch = useDebounce(search)

  const queries: Record<string, unknown> = {
    page,
    limit: DEFAULT_PAGE_SIZE,
  }
  if (debouncedSearch) queries.search = debouncedSearch
  if (activeFilter !== 'all') queries.isActive = activeFilter === 'true'

  const { data, isLoading } = useQuery<ListResponse<School>>({
    queryKey: QueryKey.SCHOOLS,
    queries,
  })

  const handleRowClick = (item: School) => {
    onEdit(item)
    openDialog(DialogId.UPDATE_SCHOOL)
  }

  const columns: ColumnDef<School>[] = [
    {
      key: 'nameEn',
      header: t('schools.nameEn'),
      render: (item) => <span className="font-medium">{item.nameEn}</span>,
    },
    {
      key: 'nameJa',
      header: t('schools.nameJa'),
      render: (item) => item.nameJa,
    },
    {
      key: 'status',
      header: t('common.status'),
      render: (item) => (
        <Badge variant={item.isActive ? 'success' : 'secondary'}>
          {item.isActive ? t('common.active') : t('common.inactive')}
        </Badge>
      ),
      className: 'w-[100px]',
    },
    {
      key: 'createdAt',
      header: t('common.createdAt'),
      render: (item) => new Date(item.createdAt).toLocaleDateString(),
      className: 'w-[150px]',
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <Input
          id="schools-search"
          placeholder={t('common.search')}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value)
            setPage(1)
          }}
          className="max-w-sm"
        />
        <Select
          value={activeFilter}
          onValueChange={(value) => {
            setActiveFilter(value)
            setPage(1)
          }}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('common.all')}</SelectItem>
            <SelectItem value="true">{t('common.active')}</SelectItem>
            <SelectItem value="false">{t('common.inactive')}</SelectItem>
          </SelectContent>
        </Select>
        <div className="ml-auto flex gap-2">
          <Button
            id="schools-sync-mirrors-button"
            variant="outline"
            onClick={() => syncMirrors(undefined)}
            loading={isSyncing}
          >
            {t('schools.syncButton')}
          </Button>
          <Button id="schools-import-open-button" variant="outline" onClick={() => openDialog(DialogId.IMPORT_SCHOOLS)}>
            {t('schools.importButton')}
          </Button>
          <Button id="schools-add-button" onClick={() => openDialog(DialogId.CREATE_SCHOOL)}>
            {t('common.create')}
          </Button>
        </div>
      </div>
      <DataTable
        columns={columns}
        data={data?.items ?? []}
        loading={isLoading}
        pagination={data?.pagination}
        onPageChange={setPage}
        onRowClick={handleRowClick}
      />
    </div>
  )
}
